/**
 * Pin operations — "Pin to Top" feature.
 *
 * Tasks pinned in a container appear in a dedicated zone at the top.
 * Each pin is stored as a setting: `#pin:{containerId}:{taskId}`.
 * The task itself is NOT modified — pin is purely visual.
 * Pins are container-scoped: a task can be pinned in one container
 * while appearing normally in date-based views.
 */

import { getDb, getNow } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { ObjectStatus } from "./db-types.js"
import type { DbObject } from "./db-types.js"

const PIN_PREFIX = "#pin:"

/**
 * Pin a task to the top of a container.
 */
export async function pinTask(
  containerId: string,
  taskId: string,
): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")

  const settingId = `${PIN_PREFIX}${containerId}:${taskId}`
  const existing = await db.setting.get(settingId)
  if (existing) return ok()

  const now = getNow()
  await db.setting.put({
    id: settingId,
    value: now,
    device: "",
    createdAt: now,
    createdBy: "",
  })

  return ok()
}

/**
 * Unpin a task from a container.
 */
export async function unpinTask(
  containerId: string,
  taskId: string,
): Promise<OpResult> {
  const db = getDb()
  await db.setting.delete(`${PIN_PREFIX}${containerId}:${taskId}`)
  return ok()
}

/**
 * Check if a task is pinned in a specific container.
 */
export async function isTaskPinned(
  containerId: string,
  taskId: string,
): Promise<boolean> {
  const db = getDb()
  return !!(await db.setting.get(`${PIN_PREFIX}${containerId}:${taskId}`))
}

export interface PinnedTask {
  task: DbObject
  pinnedAt: string
}

/**
 * Get pinned tasks for a container, sorted by value (FIFO / manual order).
 * Automatically cleans up stale entries (completed/deleted tasks).
 */
export async function getPinnedTasks(containerId: string): Promise<PinnedTask[]> {
  const db = getDb()
  const prefix = `${PIN_PREFIX}${containerId}:`
  const entries = await db.setting
    .where("id")
    .between(prefix, prefix + "\uffff", true, true)
    .toArray()

  if (entries.length === 0) return []

  // Sort by value (pinnedAt / reorder midpoint)
  entries.sort((a, b) => Number(a.value) - Number(b.value))

  // Extract task IDs from setting IDs
  const taskIds = entries.map((e) => e.id!.slice(prefix.length))
  const tasks = await Promise.all(taskIds.map((id) => db.totodo.get(id)))

  const valid: PinnedTask[] = []
  const toDelete: string[] = []

  for (let i = 0; i < tasks.length; i++) {
    const t = tasks[i]
    const entry = entries[i]
    if (t && t.status === ObjectStatus.Unfinished && !t.isDeleted) {
      valid.push({ task: t, pinnedAt: entry!.value })
    } else if (entry?.id) {
      toDelete.push(entry.id)
    }
  }

  // Lazy cleanup of stale entries
  if (toDelete.length > 0) {
    await db.setting.bulkDelete(toDelete)
  }

  return valid
}

/**
 * Reorder a pinned task within the pinned section.
 * Calculates midpoint between neighbors for the new value.
 */
export async function reorderPin(
  containerId: string,
  taskId: string,
  aboveValue: number | null,
  belowValue: number | null,
): Promise<OpResult> {
  const db = getDb()
  const settingId = `${PIN_PREFIX}${containerId}:${taskId}`
  const existing = await db.setting.get(settingId)
  if (!existing) return fail("Task is not pinned")

  const above = aboveValue ?? 0
  const below = belowValue ?? above + 1000
  const newValue = String((above + below) / 2)

  await db.setting.update(settingId, { value: newValue })
  return ok()
}

/**
 * Remove all pins for a task (across all containers).
 * Called when a task is deleted or moved to keep pins clean.
 */
export async function unpinTaskEverywhere(taskId: string): Promise<OpResult> {
  const db = getDb()
  // Pins have format #pin:{containerId}:{taskId}
  // We need to find all that end with :{taskId}
  const all = await db.setting
    .where("id")
    .startsWith(PIN_PREFIX)
    .toArray()

  const toDelete = all
    .filter((e) => e.id!.endsWith(`:${taskId}`))
    .map((e) => e.id!)

  if (toDelete.length > 0) {
    await db.setting.bulkDelete(toDelete)
  }

  return ok()
}
